import {
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
  type HTMLAttributes,
} from "react";
import { createPortal } from "react-dom";

/**
 * Get all focusable elements within a container
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
}

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Modal content */
  children: ReactNode;
  /** Modal title for accessibility */
  title: string;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Whether clicking the backdrop closes the modal */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean;
  /** Size of the modal */
  size?: "sm" | "md" | "lg" | "full";
}

/**
 * Accessible modal dialog with backdrop and focus management.
 * Features a playful, child-friendly appearance with smooth animations.
 *
 * @example
 * <Modal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   title="Great Job!"
 * >
 *   <p>You earned a star!</p>
 * </Modal>
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      children,
      title,
      showCloseButton = true,
      closeOnBackdropClick = true,
      closeOnEscape = true,
      size = "md",
      className = "",
      ...props
    },
    ref
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<Element | null>(null);

    // Size styles
    const sizeStyles = {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-2xl",
      full: "max-w-[95vw] max-h-[90vh]",
    };

    // Handle escape key and focus trap
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (closeOnEscape && event.key === "Escape") {
          onClose();
          return;
        }

        // Focus trap: Tab key cycles through focusable elements
        if (event.key === "Tab" && modalRef.current) {
          const focusableElements = getFocusableElements(modalRef.current);
          if (focusableElements.length === 0) return;

          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (!firstElement || !lastElement) return;

          if (event.shiftKey) {
            // Shift+Tab: if on first element, go to last
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab: if on last element, go to first
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }
      },
      [closeOnEscape, onClose]
    );

    // Focus management and keyboard handling
    useEffect(() => {
      if (isOpen) {
        // Store current focus
        previousActiveElement.current = document.activeElement;

        // Focus the modal
        modalRef.current?.focus();

        // Add escape listener
        document.addEventListener("keydown", handleKeyDown);

        // Prevent body scroll
        document.body.style.overflow = "hidden";

        return () => {
          document.removeEventListener("keydown", handleKeyDown);
          document.body.style.overflow = "";

          // Restore focus
          if (previousActiveElement.current instanceof HTMLElement) {
            previousActiveElement.current.focus();
          }
        };
      }
    }, [isOpen, handleKeyDown]);

    // Handle backdrop click
    const handleBackdropClick = (event: React.MouseEvent) => {
      if (closeOnBackdropClick && event.target === event.currentTarget) {
        onClose();
      }
    };

    if (!isOpen) return null;

    const modalContent = (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="presentation"
      >
        {/* Backdrop with blur */}
        <div
          className="
            absolute inset-0
            bg-night/40
            backdrop-blur-sm
            animate-[fadeIn_200ms_ease-out]
          "
          onClick={handleBackdropClick}
          aria-hidden="true"
        />

        {/* Modal container */}
        <div
          ref={(node) => {
            // Handle both refs
            (modalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          tabIndex={-1}
          className={`
            relative
            w-full ${sizeStyles[size]}
            max-h-[90vh]
            bg-cloud
            rounded-3xl
            shadow-[0_16px_48px_rgba(0,0,0,0.2),0_8px_16px_rgba(0,0,0,0.1)]
            border-[3px] border-neutral-200
            animate-[modalSlideIn_300ms_cubic-bezier(0.34,1.56,0.64,1)]
            focus:outline-none
            flex flex-col
            ${className}
          `}
          {...props}
        >
          {/* Decorative top accent */}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-24 h-2 bg-gradient-to-r from-sunshine-400 via-fairy-400 to-magic-400 rounded-full" />

          {/* Close button */}
          {showCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="
                absolute top-4 right-4 z-10
                w-12 h-12
                flex items-center justify-center
                rounded-full
                bg-neutral-100
                text-neutral-500
                border-2 border-neutral-200
                transition-all duration-200
                hover:bg-neutral-200
                hover:text-neutral-700
                hover:scale-105
                active:scale-95
                focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400
              "
              aria-label="Close modal"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          )}

          {/* Header */}
          <div className="px-6 pt-8 pb-4 flex-shrink-0">
            <h2
              id="modal-title"
              className="font-display text-display-sm text-neutral-900 text-center"
            >
              {title}
            </h2>
          </div>

          {/* Content */}
          <div className="px-6 pb-8 overflow-y-auto flex-1">{children}</div>
        </div>
      </div>
    );

    // Render in portal
    return createPortal(modalContent, document.body);
  }
);

Modal.displayName = "Modal";

/** Close icon */
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// Add keyframe animations to document head if not already present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-modal-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: scale(0.9) translateY(20px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default Modal;
